import {
  Alert, Box, Button, FormControl, FormHelperText, IconButton,
  InputAdornment, InputLabel, OutlinedInput, Paper, Snackbar, TextField, Typography
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { login } from "../../utils/AccountService";
import { useTranslation } from "react-i18next";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useNavigate } from "react-router-dom";
import Cookies from 'js-cookie';

export type UserLoginProps = {
  onLoginSuccessful: () => void;
}

const UserLogin = (props: UserLoginProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const onLoginClicked = useCallback(() => {
    const usernameMissing = !username;
    const passwordMissing = !password;
    setUsernameError(usernameMissing ? t('usernameRequired') : null);
    setPasswordError(passwordMissing ? t('passwordRequired') : null);

    if (!usernameMissing && !passwordMissing) {
      login(username, password).then(response => {
        if (response.status !== 200) { setSnackbarOpen(true); return null; }
        return response.json();
      }).then((response: any) => {
        if (response) {
          const expirationDate = new Date(new Date().getTime() + response['expiresIn']);
          Cookies.set('token', response['token'], { expires: expirationDate, secure: true });
          props.onLoginSuccessful();
        }
      });
    }
  }, [username, password, t, props]);

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.code === 'Enter' || e.code === 'NumpadEnter') onLoginClicked();
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [onLoginClicked]);

  return (
    <Box sx={{
      minHeight: 'calc(100vh - 64px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      py: 4,
    }}>
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 400, borderRadius: 3 }}>
        {/* Brand */}
        <Box sx={{ textAlign: 'center', mb: 3.5 }}>
          <Box sx={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'linear-gradient(135deg, #D97706 0%, #92400E 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 1.5,
            boxShadow: '0 8px 20px rgba(180, 83, 9, 0.3)',
          }}>
            <MenuBookIcon sx={{ color: '#fff', fontSize: 32 }} />
          </Box>
          <Typography variant="h5" fontWeight={800} gutterBottom>
            Cookbook
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('login')}
          </Typography>
        </Box>

        {/* Fields */}
        <TextField
          fullWidth
          label={t('username')}
          variant="outlined"
          sx={{ mb: 2 }}
          error={!!usernameError}
          helperText={usernameError}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
        />

        <FormControl fullWidth variant="outlined" error={!!passwordError}>
          <InputLabel>{t('password')}</InputLabel>
          <OutlinedInput
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(s => !s)}
                  onMouseDown={(e) => e.preventDefault()}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            label={t('password')}
          />
          {passwordError && <FormHelperText>{passwordError}</FormHelperText>}
        </FormControl>

        {/* Actions */}
        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          startIcon={<LoginIcon />}
          onClick={onLoginClicked}
          sx={{ mt: 3, py: 1.4 }}
        >
          {t('login')}
        </Button>

        <Button
          fullWidth
          variant="outlined"
          color="primary"
          size="large"
          startIcon={<PersonAddIcon />}
          onClick={() => navigate('/register')}
          sx={{ mt: 1.5, py: 1.4 }}
        >
          {t('register')}
        </Button>
      </Paper>

      <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={() => setSnackbarOpen(false)}>
          {t('loginFailure')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserLogin;
